"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { Controller, type Resolver, useForm } from "react-hook-form";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import type { Client } from "@/lib/schemas/paycode/client";
import {
  buildDynamicFieldsShape,
  collectDynamicFieldValues,
  type DynamicField,
  dynamicFieldDefaults,
} from "@/lib/schemas/paycode/dynamic-field";
import { useCheckoutStore } from "@/lib/store/checkout";
import { Label } from "../ui/label";
import { Skeleton } from "../ui/skeleton";
import { ContinueButton } from "./continue-button";
import { DynamicFields } from "./dynamic-fields";

type CustomerTranslator = (
  key: string,
  values?: Record<string, string | number>,
) => string;

export const makeCustomerSchema = (t: CustomerTranslator) =>
  z.object({
    name: z
      .string()
      .min(1, t("errors.nameRequired"))
      .regex(/^[\p{L}\s.'-]+$/u, t("errors.nameInvalid")),
    paternalSurname: z
      .string()
      .min(1, t("errors.paternalSurnameRequired"))
      .regex(/^[\p{L}\s.'-]+$/u, t("errors.surnameInvalid")),
    maternalSurname: z
      .string()
      .min(1, t("errors.maternalSurnameRequired"))
      .regex(/^[\p{L}\s.'-]+$/u, t("errors.surnameInvalid")),
    email: z
      .string()
      .min(1, t("errors.emailRequired"))
      .email(t("errors.emailInvalid")),
    phone: z
      .string()
      .min(1, t("errors.phoneRequired"))
      .refine(
        (v) => v.replace(/\D/g, "").length === 10,
        t("errors.phoneInvalid"),
      ),
  });

export type CustomerData = z.infer<ReturnType<typeof makeCustomerSchema>>;

type CustomerFormValues = Record<string, string>;

const EMPTY_FIELDS: DynamicField[] = [];

export const toClient = (data: CustomerData): Client => ({
  name: data.name.trim(),
  paternalSurname: data.paternalSurname.trim(),
  maternalSurname: data.maternalSurname.trim(),
  alias: null,
  email: data.email.trim(),
  phone: data.phone.replace(/\D/g, ""),
  fullName: `${data.name.trim()} ${data.paternalSurname.trim()} ${data.maternalSurname.trim()}`,
  tokens: undefined,
});

export const CustomerForm = () => {
  const t = useTranslations("customer");
  const setClient = useCheckoutStore((s) => s.setClient);
  const setDynamicFields = useCheckoutStore((s) => s.setDynamicFields);
  const completeCurrentStep = useCheckoutStore((s) => s.completeCurrentStep);
  const client = useCheckoutStore((s) => s.client);
  const dynamicFields =
    useCheckoutStore((s) => s.dynamicFields) ?? EMPTY_FIELDS;

  const schema = useMemo(
    () =>
      makeCustomerSchema(t).extend(buildDynamicFieldsShape(dynamicFields, t)),
    [dynamicFields, t],
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CustomerFormValues>({
    resolver: zodResolver(schema) as Resolver<CustomerFormValues>,
    mode: "onSubmit",
    defaultValues: {
      name: client?.name ?? "",
      paternalSurname: client?.paternalSurname ?? "",
      maternalSurname: client?.maternalSurname ?? "",
      email: client?.email ?? "",
      phone: client?.phone ?? "",
      ...dynamicFieldDefaults(dynamicFields),
    },
  });

  const onSubmit = (data: CustomerFormValues) => {
    setClient(toClient(data as CustomerData));
    setDynamicFields(collectDynamicFieldValues(dynamicFields, data));
    completeCurrentStep();
  };

  return (
    <form
      className="flex flex-col gap-2 pb-4"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="flex flex-col gap-1.5">
        <Label className="ml-2" htmlFor="name">
          {t("name")}
        </Label>
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <Input
              id="name"
              placeholder={t("name")}
              autoComplete="given-name"
              aria-invalid={!!errors.name}
              {...field}
            />
          )}
        />
        {errors.name ? (
          <p className="text-destructive text-sm">{errors.name.message}</p>
        ) : null}
      </div>

      <div className="flex flex-col gap-2 md:flex-row">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="paternalSurname">{t("paternalSurname")}</Label>
          <Controller
            control={control}
            name="paternalSurname"
            render={({ field }) => (
              <Input
                id="paternalSurname"
                placeholder={t("paternalSurname")}
                autoComplete="family-name"
                aria-invalid={!!errors.paternalSurname}
                {...field}
              />
            )}
          />
          {errors.paternalSurname ? (
            <p className="text-destructive text-sm">
              {errors.paternalSurname.message}
            </p>
          ) : null}
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="maternalSurname">{t("maternalSurname")}</Label>
          <Controller
            control={control}
            name="maternalSurname"
            render={({ field }) => (
              <Input
                id="maternalSurname"
                placeholder={t("maternalSurname")}
                autoComplete="additional-name"
                aria-invalid={!!errors.maternalSurname}
                {...field}
              />
            )}
          />
          {errors.maternalSurname ? (
            <p className="text-destructive text-sm">
              {errors.maternalSurname.message}
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="email">{t("email")}</Label>
        <Controller
          control={control}
          name="email"
          render={({ field }) => (
            <Input
              id="email"
              type="email"
              placeholder={t("emailPlaceholder")}
              autoComplete="email"
              aria-invalid={!!errors.email}
              {...field}
            />
          )}
        />
        {errors.email ? (
          <p className="text-destructive text-sm">{errors.email.message}</p>
        ) : null}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="phone">{t("phone")}</Label>
        <Controller
          control={control}
          name="phone"
          render={({ field }) => (
            <Input
              id="phone"
              type="tel"
              inputMode="tel"
              placeholder={t("phonePlaceholder")}
              autoComplete="tel"
              maxLength={10}
              aria-invalid={!!errors.phone}
              {...field}
              onChange={(e) =>
                field.onChange(e.target.value.replace(/\D/g, "").slice(0, 10))
              }
            />
          )}
        />
        {errors.phone ? (
          <p className="text-destructive text-sm">{errors.phone.message}</p>
        ) : null}
      </div>

      <DynamicFields fields={dynamicFields} control={control} errors={errors} />

      <motion.div
        layout="position"
        layoutId="continue-button"
        className="self-center"
        transition={{ duration: 0.18, ease: "easeOut" }}
      >
        <ContinueButton />
      </motion.div>
    </form>
  );
};

const FieldSkeleton = ({ className }: { className?: string }) => (
  <div className={`flex flex-col gap-1.5 ${className ?? ""}`}>
    <Skeleton className="ml-2 h-4 w-20" />
    <Skeleton className="h-12 w-full rounded-lg" />
  </div>
);

export const CustomerFormSkeleton = ({
  dynamicFields = 0,
}: {
  dynamicFields?: number;
}) => (
  <div className="flex flex-col gap-2 pb-4">
    <FieldSkeleton />
    <div className="flex flex-col gap-2 md:flex-row">
      <FieldSkeleton className="flex-1" />
      <FieldSkeleton className="flex-1" />
    </div>
    <FieldSkeleton />
    <FieldSkeleton />
    {Array.from({ length: dynamicFields }, (_, i) => (
      // biome-ignore lint/suspicious/noArrayIndexKey: identical static skeleton items
      <FieldSkeleton key={`sk-dynamic-${i}`} />
    ))}
    <Skeleton className="mt-4 h-12 w-36 self-center rounded-full" />
  </div>
);
